import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageCircle, Send, Edit2, Trash2, X, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ToolComment } from '@/types/tool';
import { useTranslation } from 'react-i18next';

interface CommentsSectionProps {
  comments: ToolComment[];
  newComment: string;
  submittingComment: boolean;
  user: any;
  prefix: string;
  onCommentChange: (value: string) => void;
  onSubmitComment: () => void;
  onNavigateToAuth: () => void;
  onEditComment: (commentId: string, newText: string) => void;
  onDeleteComment: (commentId: string) => void;
}

export const CommentsSection: React.FC<CommentsSectionProps> = ({
  comments,
  newComment,
  submittingComment,
  user,
  prefix,
  onCommentChange,
  onSubmitComment,
  onNavigateToAuth,
  onEditComment,
  onDeleteComment
}) => {
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const { t } = useTranslation();

  return (
    <Card className="shadow-elegant">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          {t('tool.detail.comments.title', { count: comments.length })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Add Comment */}
        {user ? (
          <div className="mb-6">
            <Textarea
              placeholder="Share your thoughts about this tool..."
              value={newComment}
              onChange={(e) => onCommentChange(e.target.value)}
              className="mb-3"
              rows={3}
            />
            <Button 
              onClick={onSubmitComment}
              disabled={!newComment.trim() || submittingComment}
              className="gap-2"
            >
              <Send className="w-4 h-4" />
              {submittingComment ? 'Posting...' : 'Post Comment'}
            </Button>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-muted rounded-lg text-center">
            <p className="text-muted-foreground">
              <Button variant="link" onClick={onNavigateToAuth} className="p-0">
                {t('generic.signIn')}
              </Button>
              {' '}{t('tool.detail.comments.toLeaveComment')}
            </p>
          </div>
        )}

        {/* Comments List */}
        <div className="space-y-4">
          {comments.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {t('tool.detail.comments.noCommentsYet')}
            </p>
          ) : (
            comments.map((comment) => {
              const isOwner = user?.id === comment.user_id;
              const isEditing = editingCommentId === comment.id;

              const handleEditClick = () => {
                setEditingCommentId(comment.id);
                setEditText(comment.comment_text);
              };

              const handleSaveEdit = () => {
                onEditComment(comment.id, editText);
                setEditingCommentId(null);
                setEditText('');
              };

              const handleCancelEdit = () => {
                setEditingCommentId(null);
                setEditText('');
              };

              return (
                <div key={comment.id} className="flex gap-3 p-4 bg-muted/30 rounded-lg">
                  <Avatar>
                    {comment.profiles?.avatar_url && (
                      <AvatarImage src={comment.profiles.avatar_url} alt={comment.profiles?.display_name || 'User'} />
                    )}
                    <AvatarFallback>
                      {comment.profiles?.display_name?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {comment.profiles?.display_name || t('generic.anonymousUser')}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      {isOwner && !isEditing && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleEditClick}
                            className="h-6 w-6 p-0"
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteComment(comment.id)}
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="text-sm"
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={!editText.trim()}
                            className="h-6 px-2 text-xs"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            className="h-6 px-2 text-xs"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm">{comment.comment_text}</p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}; 